import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { systemApi } from "../services/api";
import { getWebChangelog } from "../services/changelog";
import type { ChangelogDocument, ChangelogEntry } from "../services/changelog";

type DisplayEntry = ChangelogEntry & { repository: "web" | "api" };

const typeLabelKeys: Record<ChangelogEntry["type"], string> = {
  feat: "changelog.typeFeat",
  fix: "changelog.typeFix",
  perf: "changelog.typePerf",
  refactor: "changelog.typeRefactor",
  revert: "changelog.typeRevert",
};

const typeClasses: Record<ChangelogEntry["type"], string> = {
  feat: "text-bg-success",
  fix: "text-bg-warning",
  perf: "text-bg-info",
  refactor: "text-bg-secondary",
  revert: "text-bg-danger",
};

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

export function Changelog() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<ChangelogDocument[]>([]);
  const [failedSources, setFailedSources] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.document.title = t("changelog.documentTitle");
    Promise.allSettled([getWebChangelog(), systemApi.getChangelog()])
      .then((results) => {
        const fulfilled = results.flatMap((result) =>
          result.status === "fulfilled" ? [result.value] : [],
        );
        setDocuments(fulfilled);
        setFailedSources(results.length - fulfilled.length);
      })
      .finally(() => setLoading(false));
  }, [t]);

  const groups = useMemo(() => {
    const entries = documents
      .flatMap((document) =>
        document.entries.map((entry) => ({
          ...entry,
          repository: document.repository,
        })),
      )
      .sort((left, right) => Date.parse(right.date) - Date.parse(left.date));

    return entries.reduce<Array<{ date: string; entries: DisplayEntry[] }>>(
      (result, entry) => {
        const date = entry.date.slice(0, 10);
        const latest = result[result.length - 1];
        if (latest?.date === date) {
          latest.entries.push(entry);
        } else {
          result.push({ date, entries: [entry] });
        }
        return result;
      },
      [],
    );
  }, [documents]);

  const stagingSources = documents
    .filter((document) => document.historyScope === "staging")
    .map((document) =>
      document.repository === "web"
        ? t("changelog.repoWeb")
        : t("changelog.repoApi"),
    );
  const stagingSourcesLabel = stagingSources.join(` ${t("changelog.and")} `);

  return (
    <div className="min-vh-100 bg-light d-flex flex-column">
      <header className="bg-white border-bottom py-3">
        <div className="container d-flex align-items-center justify-content-between">
          <Link to="/" aria-label={t("changelog.logoAriaLabel")}>
            <img src="/assets/images/logo-full.png" alt="ETAPPIO" height="38" />
          </Link>
          <Link to="/" className="btn btn-light-brand btn-sm">
            {t("changelog.toHomepage")}
          </Link>
        </div>
      </header>

      <main
        className="container py-5 flex-grow-1"
        style={{ maxWidth: "920px" }}
      >
        <div className="mb-4">
          <h1 className="fw-bold mb-2">{t("changelog.title")}</h1>
          <p className="text-muted mb-0">{t("changelog.subtitle")}</p>
        </div>

        {!loading && stagingSources.length > 0 && (
          <div className="alert alert-info" role="status">
            <strong>
              {t("changelog.stagingFilterHeading", {
                sources: stagingSourcesLabel,
              })}
            </strong>{" "}
            {t("changelog.stagingFilterDetailBefore")}
            <code>develop</code>
            {t("changelog.stagingFilterDetailAfter")}
          </div>
        )}

        {loading && (
          <div
            className="d-flex align-items-center gap-2 text-muted py-5"
            role="status"
          >
            <span
              className="spinner-border spinner-border-sm"
              aria-hidden="true"
            ></span>
            {t("changelog.loading")}
          </div>
        )}

        {!loading && failedSources > 0 && documents.length > 0 && (
          <div className="alert alert-warning" role="alert">
            {t("changelog.partialError")}
          </div>
        )}

        {!loading && documents.length === 0 && (
          <div className="alert alert-danger" role="alert">
            {t("changelog.loadError")}
          </div>
        )}

        {!loading && documents.length > 0 && groups.length === 0 && (
          <div className="alert alert-info" role="status">
            {t("changelog.empty")}
          </div>
        )}

        {groups.map((group) => (
          <section key={group.date} className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-white py-3">
              <h2 className="h5 mb-0">{formatDate(group.date)}</h2>
            </div>
            <ul className="list-group list-group-flush">
              {group.entries.map((entry) => (
                <li
                  className="list-group-item px-4 py-3"
                  key={`${entry.repository}-${entry.commit}`}
                >
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <span className={`badge ${typeClasses[entry.type]}`}>
                      {t(typeLabelKeys[entry.type])}
                    </span>
                    <span className="badge text-bg-light border">
                      {entry.repository === "web"
                        ? t("changelog.repoWeb")
                        : t("changelog.repoApi")}
                    </span>
                    {entry.scope && <code>{entry.scope}</code>}
                    {entry.breaking && (
                      <span className="badge text-bg-danger">
                        {t("changelog.breaking")}
                      </span>
                    )}
                  </div>
                  <p className="mb-1">{entry.description}</p>
                  <small className="text-muted">
                    <code>{entry.commit.slice(0, 7)}</code>
                  </small>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </main>
    </div>
  );
}
